import {
  TaskRule,
  TaskRuleAction,
  TaskRuleActionType,
  TaskRuleCondition,
  TaskRuleDefinition,
  TaskRuleField,
  TaskRuleOperator,
  TaskRuleRecipient,
  TaskRuleUnit,
} from 'src/app/interfaces/entities/task-rule';
import { TaskTypeEnum } from 'src/app/pages/config/project-config/project-structure-config/project-task-config/datatypes-taskconfig';

/**
 * What the rule editor offers per task type (#4963/#4964). Twin of TaskRuleCatalog.cs: the API
 * checks the same, so a dropdown never offers something the save would reject.
 */

/** What the value part of a condition row looks like. */
export type ValueKind =
  | 'none'
  | 'duration'
  | 'durationStatus'
  | 'status'
  | 'number'
  | 'numberRange'
  | 'text'
  | 'option'
  | 'contact'
  | 'contractType'
  | 'contractStatus';

export interface Option<T = string> {
  value: T;
  text: string;
}

export const YES = 'Ja';
export const NO = 'Nein';

export const STATUSES: Option[] = [
  { value: 'OPEN', text: $localize`:@@ruleStatusOpen:Open` },
  { value: 'IN_PROGRESS', text: $localize`:@@ruleStatusInProgress:In progress` },
  { value: 'IN_REVIEW', text: $localize`:@@ruleStatusInReview:In review` },
  { value: 'DONE', text: $localize`:@@ruleStatusDone:Done` },
  { value: 'REJECTED', text: $localize`:@@ruleStatusRejected:Rejected` },
  { value: 'NOT_REQUIRED', text: $localize`:@@ruleStatusNotRequired:Not required` },
];
export const DONE_STATUSES = ['DONE', 'NOT_REQUIRED'];

export const UNITS: Option<TaskRuleUnit>[] = [
  { value: 'Days', text: $localize`:@@ruleUnitDays:Days` },
  { value: 'Weeks', text: $localize`:@@ruleUnitWeeks:Weeks` },
  { value: 'Months', text: $localize`:@@ruleUnitMonths:Months` },
];

export const PRIORITIES: Option<number>[] = [
  { value: 0, text: $localize`:@@critical:Critical` },
  { value: 1, text: $localize`:@@high:High` },
  { value: 2, text: $localize`:@@medium:Medium` },
  { value: 3, text: $localize`:@@low:Low` },
];

export const MICROTASK_TYPES: Option[] = [
  { value: TaskTypeEnum.Task, text: $localize`:@@task:Task` },
  { value: TaskTypeEnum.YesNo, text: $localize`:@@yesno:Yes/No` },
  { value: TaskTypeEnum.Date, text: $localize`:@@date:Date` },
  { value: TaskTypeEnum.Text, text: $localize`:@@text:Text` },
  { value: TaskTypeEnum.Number, text: $localize`:@@number:Number` },
];

const FIELD_LABELS: Record<TaskRuleField, string> = {
  DueDate: $localize`:@@ruleFieldDueDate:Due date`,
  Status: $localize`:@@ruleFieldStatus:Status`,
  Responsible: $localize`:@@ruleFieldResponsible:Responsible person`,
  Subtasks: $localize`:@@ruleFieldSubtasks:Subtasks`,
  Date: $localize`:@@ruleFieldDate:Entered date`,
  Number: $localize`:@@ruleFieldNumber:Entered number`,
  Text: $localize`:@@ruleFieldText:Entered text`,
  YesNo: $localize`:@@ruleFieldYesNo:Answer`,
  Contact: $localize`:@@ruleFieldContact:Entered contact`,
  Selection: $localize`:@@ruleFieldSelection:Option`,
  ContractType: $localize`:@@ruleFieldContractType:Contract type`,
  ContractStatus: $localize`:@@ruleFieldContractStatus:Contract status`,
  ContractPartyInternal: $localize`:@@ruleFieldPartyInternal:Contract party internal`,
  ContractPartyExternal: $localize`:@@ruleFieldPartyExternal:Contract party external`,
  OtherTask: $localize`:@@ruleFieldOtherTask:Other task`,
};

/** The field as subject of a preview sentence ("the due date …"). */
const SUBJECT_LABELS: Partial<Record<TaskRuleField, string>> = {
  DueDate: $localize`:@@ruleSubjDueDate:the due date`,
  Status: $localize`:@@ruleSubjStatus:the status`,
  Date: $localize`:@@ruleSubjDate:the entered date`,
  Number: $localize`:@@ruleSubjNumber:the entered number`,
  Text: $localize`:@@ruleSubjText:the entered text`,
  YesNo: $localize`:@@ruleSubjYesNo:the answer`,
  Contact: $localize`:@@ruleSubjContact:the entered contact`,
  Selection: $localize`:@@ruleSubjSelection:the chosen option`,
  ContractType: $localize`:@@ruleSubjContractType:the contract type`,
  ContractStatus: $localize`:@@ruleSubjContractStatus:the contract status`,
  ContractPartyInternal: $localize`:@@ruleSubjPartyInternal:the internal contract party`,
  ContractPartyExternal: $localize`:@@ruleSubjPartyExternal:the external contract party`,
};

const OPERATOR_LABELS: Record<TaskRuleOperator, string> = {
  InLessThan: $localize`:@@ruleOpInLessThan:is in less than`,
  IsToday: $localize`:@@ruleOpIsToday:is today`,
  OverdueSince: $localize`:@@ruleOpOverdueSince:is overdue for at least`,
  Is: $localize`:@@ruleOpIs:is`,
  IsNot: $localize`:@@ruleOpIsNot:is not`,
  SinceAtLeast: $localize`:@@ruleOpSinceAtLeast:has been … for at least`,
  IsReset: $localize`:@@ruleOpIsReset:has been reset`,
  NotAssigned: $localize`:@@ruleOpNotAssigned:is not assigned`,
  NotAssignedSince: $localize`:@@ruleOpNotAssignedSince:is not assigned for`,
  AllDone: $localize`:@@ruleOpAllDone:are all done`,
  BeforeDueDate: $localize`:@@ruleOpBeforeDueDate:is before the due date`,
  AfterDueDate: $localize`:@@ruleOpAfterDueDate:is after the due date`,
  Gt: $localize`:@@ruleOpGt:greater`,
  Ge: $localize`:@@ruleOpGe:greater or equal`,
  Lt: $localize`:@@ruleOpLt:less`,
  Le: $localize`:@@ruleOpLe:less or equal`,
  Eq: $localize`:@@ruleOpEq:equal`,
  Between: $localize`:@@ruleOpBetween:between`,
  Contains: $localize`:@@ruleOpContains:contains`,
  NotContains: $localize`:@@ruleOpNotContains:does not contain`,
  IsYes: $localize`:@@ruleOpIsYes:is Yes`,
  IsNo: $localize`:@@ruleOpIsNo:is No`,
};

export const ACTION_LABELS: Record<TaskRuleActionType, string> = {
  Notify: $localize`:@@ruleActionNotify:send notification to`,
  SetStatus: $localize`:@@ruleActionSetStatus:set status`,
  SetResponsible: $localize`:@@ruleActionSetResponsible:set responsible person`,
  SetResult: $localize`:@@ruleActionSetResult:set result`,
  SetPriority: $localize`:@@ruleActionSetPriority:set priority`,
  CreateMicrotask: $localize`:@@ruleActionCreateMicrotask:create a microtask`,
};

export const RECIPIENTS: Option<TaskRuleRecipient>[] = [
  { value: 'Responsible', text: $localize`:@@ruleRecipientResponsible:Assigned person` },
  { value: 'Manager', text: $localize`:@@ruleRecipientManager:Manager (Reports to)` },
  { value: 'TaskDepartment', text: $localize`:@@ruleRecipientTaskDepartment:Department of the task` },
  { value: 'Department', text: $localize`:@@ruleRecipientDepartment:Other department` },
  { value: 'User', text: $localize`:@@ruleRecipientUser:Fixed user` },
];

type OperatorMap = Partial<Record<TaskRuleOperator, ValueKind>>;

const NUMBER_OPS: OperatorMap = { Gt: 'number', Ge: 'number', Lt: 'number', Le: 'number', Eq: 'number', Between: 'numberRange' };
const DATE_OPS: OperatorMap = { InLessThan: 'duration', OverdueSince: 'duration', BeforeDueDate: 'none', AfterDueDate: 'none' };
const TEXT_OPS: OperatorMap = { Contains: 'text', NotContains: 'text' };
const YES_NO_OPS: OperatorMap = { IsYes: 'none', IsNo: 'none' };
const SELECTION_OPS: OperatorMap = { Is: 'option', IsNot: 'option' };
const CONTACT_OPS: OperatorMap = { Is: 'contact' };
const STATUS_IS_OPS: OperatorMap = { Is: 'status', IsNot: 'status' };

/** Own-task fields of a task type with their operators, in display order. */
export function fieldsFor(taskType: string): Partial<Record<TaskRuleField, OperatorMap>> {
  const fields: Partial<Record<TaskRuleField, OperatorMap>> = {
    DueDate: { InLessThan: 'duration', IsToday: 'none', OverdueSince: 'duration' },
    Status: { Is: 'status', IsNot: 'status', SinceAtLeast: 'durationStatus', IsReset: 'none' },
  };
  if (taskType === TaskTypeEnum.Group) fields.Subtasks = { AllDone: 'none' };
  else fields.Responsible = { NotAssigned: 'none', NotAssignedSince: 'duration' };

  switch (taskType) {
    case TaskTypeEnum.Date:
      fields.Date = DATE_OPS;
      break;
    case TaskTypeEnum.Number:
      fields.Number = NUMBER_OPS;
      break;
    case TaskTypeEnum.Text:
      fields.Text = TEXT_OPS;
      break;
    case TaskTypeEnum.YesNo:
      fields.YesNo = YES_NO_OPS;
      break;
    case TaskTypeEnum.Contact:
      fields.Contact = CONTACT_OPS;
      break;
    case TaskTypeEnum.UserDefinedFields:
      fields.Selection = SELECTION_OPS;
      break;
    case TaskTypeEnum.Contract:
      fields.ContractType = { Is: 'contractType' };
      fields.ContractStatus = { Is: 'contractStatus' };
      fields.ContractPartyInternal = CONTACT_OPS;
      fields.ContractPartyExternal = CONTACT_OPS;
      break;
  }
  fields.OtherTask = {};
  return fields;
}

export function otherTaskOperators(otherType: string | undefined, property: 'Status' | 'Result' | null | undefined): OperatorMap {
  if (property !== 'Result') return STATUS_IS_OPS;
  switch (otherType) {
    case TaskTypeEnum.Number:
      return NUMBER_OPS;
    case TaskTypeEnum.YesNo:
      return YES_NO_OPS;
    case TaskTypeEnum.UserDefinedFields:
      return SELECTION_OPS;
    case TaskTypeEnum.Contact:
      return CONTACT_OPS;
    case TaskTypeEnum.Text:
      return TEXT_OPS;
    case TaskTypeEnum.Date:
      return DATE_OPS;
    default:
      return {};
  }
}

export const hasResult = (taskType: string | undefined) => Object.keys(otherTaskOperators(taskType, 'Result')).length > 0;

export const VALUE_FIELDS: TaskRuleField[] = [
  'Date',
  'Number',
  'Text',
  'YesNo',
  'Contact',
  'Selection',
  'ContractType',
  'ContractStatus',
  'ContractPartyInternal',
  'ContractPartyExternal',
];

export const canSetResult = (taskType: string) =>
  [TaskTypeEnum.YesNo, TaskTypeEnum.UserDefinedFields, TaskTypeEnum.Number, TaskTypeEnum.Text].includes(taskType as TaskTypeEnum);

export function actionsFor(taskType: string): TaskRuleActionType[] {
  const actions: TaskRuleActionType[] = ['Notify', 'SetStatus'];
  if (taskType !== TaskTypeEnum.Group) {
    actions.push('SetResponsible');
    if (canSetResult(taskType)) actions.push('SetResult');
    actions.push('SetPriority');
  }
  actions.push('CreateMicrotask');
  return actions;
}

/** "set status": Done only for plain tasks (other types would lack a value), never In review / Rejected. */
export function settableStatuses(taskType: string): Option[] {
  const values = ['OPEN', 'IN_PROGRESS', 'NOT_REQUIRED', ...(taskType === TaskTypeEnum.Task ? ['DONE'] : [])];
  return STATUSES.filter(s => values.includes(s.value));
}

export const fieldLabel = (field: TaskRuleField) => FIELD_LABELS[field];
export const operatorLabel = (op: TaskRuleOperator) => OPERATOR_LABELS[op];
export const statusLabel = (status: string | null | undefined) => STATUSES.find(s => s.value === status)?.text ?? status ?? '';

export const isOneTimeRule = (d: TaskRuleDefinition) =>
  d.groups.some(g =>
    g.conditions.some(
      c => VALUE_FIELDS.includes(c.field as TaskRuleField) || (c.field === 'Status' && (c.operator === 'Is' || c.operator === 'SinceAtLeast') && DONE_STATUSES.includes(c.value ?? ''))
    )
  );
export const hasNotify = (d: TaskRuleDefinition) => d.actions.some(a => a.type === 'Notify');

/* ===================== new objects ===================== */

export const newCondition = (): TaskRuleCondition => ({ field: null, operator: null, unit: 'Days' });
export const newAction = (type: TaskRuleActionType | null = 'Notify'): TaskRuleAction => ({ type });

export function newRule(): TaskRule {
  return {
    id: null,
    name: '',
    isActive: true,
    definition: {
      groups: [{ conditions: [newCondition()] }],
      actions: [newAction()],
      timing: { trigger: 'Immediately', delayUnit: 'Days', repeat: 'Once' },
    },
  };
}

export const cloneRule = (rule: TaskRule): TaskRule => JSON.parse(JSON.stringify(rule));

/* ===================== context the editor works in ===================== */

export interface RuleTaskInfo {
  id?: number;
  type: string;
  title?: string;
  nr?: string;
  validation?: boolean;
  options: string[];
}

export interface RuleEditorContext {
  task: RuleTaskInfo;
  otherTasks: RuleTaskInfo[];
  departments: { id: number; name: string }[];
  users: { emailAddress: string; displayName: string }[];
  taskPackages: { id: number; name: string }[];
  contractTypes: { id: number; name: string }[];
  contractStatuses: { id: number; status: string }[];
  contactName: (id: string | null | undefined, type: string | null | undefined) => string | null;
}

/* ===================== validation (same checks as the API) ===================== */

const blank = (v: unknown) => v === null || v === undefined || (typeof v === 'string' && v.trim() === '');
const isNumber = (v: unknown) => !blank(v) && !isNaN(Number(String(v).replace(',', '.')));
/** Amounts, days and counts are `int?` on the backend: 1.5 would fail the whole task save. */
const notWhole = (v: unknown) => !blank(v) && !Number.isInteger(Number(v));

export const ERR = {
  field: $localize`:@@ruleErrField:Please choose a field`,
  task: $localize`:@@ruleErrTask:Please choose a task`,
  self: $localize`:@@ruleErrSelf:Points to this task itself – please use the Status condition`,
  operator: $localize`:@@ruleErrOperator:Please choose an operator`,
  amount: $localize`:@@ruleErrAmount:Please enter a number`,
  wholeNumber: $localize`:@@ruleErrWholeNumber:Please enter a whole number`,
  value: $localize`:@@ruleErrValue:Please choose a value`,
  notAvailable: $localize`:@@ruleErrNotAvailable:Not available for this task`,
  action: $localize`:@@ruleErrAction:Please choose an action`,
  recipient: $localize`:@@ruleErrRecipient:Please choose a recipient`,
  department: $localize`:@@ruleErrDepartment:Please choose a department`,
  user: $localize`:@@ruleErrUser:Please choose a user`,
  status: $localize`:@@ruleErrStatus:Please choose a status`,
  result: $localize`:@@ruleErrResult:Please enter a result`,
  priority: $localize`:@@ruleErrPriority:Please choose a priority`,
  microtask: $localize`:@@ruleErrMicrotask:Please fill in all required fields`,
  name: $localize`:@@ruleErrName:Name is required`,
  noConditions: $localize`:@@ruleErrNoConditions:At least one condition is required.`,
  noActions: $localize`:@@ruleErrNoActions:At least one action is required.`,
  delay: $localize`:@@ruleErrDelay:Please enter a number of at least 1`,
  everyDays: $localize`:@@ruleErrEveryDays:At least 2 days`,
  max: $localize`:@@ruleErrMax:At least 2`,
};

export function conditionOperators(c: TaskRuleCondition, ctx: RuleEditorContext): OperatorMap {
  if (c.field === 'OtherTask') {
    const other = ctx.otherTasks.find(t => t.id === c.otherTaskConfigId);
    return other ? otherTaskOperators(other.type, c.property) : {};
  }
  return (c.field && fieldsFor(ctx.task.type)[c.field]) || {};
}

export const valueKindOf = (c: TaskRuleCondition, ctx: RuleEditorContext): ValueKind | null =>
  c.operator ? (conditionOperators(c, ctx)[c.operator] ?? null) : null;

export function conditionError(c: TaskRuleCondition, ctx: RuleEditorContext): string | null {
  if (!c.field) return ERR.field;
  if (!(c.field in fieldsFor(ctx.task.type))) return ERR.notAvailable;
  if (c.field === 'OtherTask') {
    if (c.otherTaskConfigId == null) return ERR.task;
    if (ctx.task.id != null && c.otherTaskConfigId === ctx.task.id) return ERR.self;
    if (!ctx.otherTasks.some(t => t.id === c.otherTaskConfigId)) return ERR.notAvailable;
  }
  const kind = valueKindOf(c, ctx);
  if (!c.operator) return ERR.operator;
  if (kind == null) return ERR.notAvailable;
  switch (kind) {
    case 'duration':
      return blank(c.amount) || Number(c.amount) < 0 ? ERR.amount : notWhole(c.amount) ? ERR.wholeNumber : null;
    case 'durationStatus':
      return blank(c.amount) ? ERR.amount : notWhole(c.amount) ? ERR.wholeNumber : blank(c.value) ? ERR.value : null;
    case 'status':
    case 'option':
    case 'contractType':
    case 'contractStatus':
      if (blank(c.value)) return ERR.value;
      if (kind === 'option' && c.field === 'Selection' && !ctx.task.options.includes(c.value!)) return ERR.notAvailable;
      return null;
    case 'number':
      return isNumber(c.value) ? null : ERR.amount;
    case 'numberRange':
      return isNumber(c.value) && isNumber(c.value2) ? null : ERR.amount;
    case 'text':
      return blank(c.value) ? ERR.value : null;
    case 'contact':
      return blank(c.value) ? ERR.value : null;
  }
  return null;
}

export function actionError(a: TaskRuleAction, ctx: RuleEditorContext): string | null {
  if (!a.type) return ERR.action;
  if (!actionsFor(ctx.task.type).includes(a.type)) return ERR.notAvailable;
  switch (a.type) {
    case 'Notify':
      if (!a.recipient) return ERR.recipient;
      if (a.recipient === 'Department' && a.departmentId == null) return ERR.department;
      if (a.recipient === 'User' && blank(a.userEmail)) return ERR.user;
      return null;
    case 'SetStatus':
      return settableStatuses(ctx.task.type).some(s => s.value === a.status) ? null : ERR.status;
    case 'SetResponsible':
      return blank(a.userEmail) && a.departmentId == null ? ERR.user : null;
    case 'SetResult':
      if (blank(a.value)) return ERR.result;
      if (ctx.task.type === TaskTypeEnum.Number && !isNumber(a.value)) return ERR.amount;
      if (ctx.task.type === TaskTypeEnum.UserDefinedFields && !ctx.task.options.includes(a.value!)) return ERR.notAvailable;
      return null;
    case 'SetPriority':
      return a.priority == null ? ERR.priority : null;
    case 'CreateMicrotask':
      return blank(a.title) || a.taskPackageId == null || blank(a.taskType) || a.departmentId == null ? ERR.microtask : null;
  }
  return null;
}

export interface TimingErrors {
  delay?: string;
  everyDays?: string;
  max?: string;
  interval?: string;
}

export function timingErrors(rule: TaskRule): TimingErrors {
  const t = rule.definition.timing;
  const errors: TimingErrors = {};
  if (t.trigger === 'Delayed' && (blank(t.delayAmount) || Number(t.delayAmount) < 1)) errors.delay = ERR.delay;
  else if (t.trigger === 'Delayed' && notWhole(t.delayAmount)) errors.delay = ERR.wholeNumber;
  if (repetitionLocked(rule) || t.repeat !== 'Repeat') return errors;
  if (!t.interval) errors.interval = ERR.value;
  if (t.interval === 'Custom' && (blank(t.everyDays) || Number(t.everyDays) < 2)) errors.everyDays = ERR.everyDays;
  else if (t.interval === 'Custom' && notWhole(t.everyDays)) errors.everyDays = ERR.wholeNumber;
  if (blank(t.maxNotifications) || Number(t.maxNotifications) < 2) errors.max = ERR.max;
  else if (notWhole(t.maxNotifications)) errors.max = ERR.wholeNumber;
  return errors;
}

/** Repetition is greyed out and fixed to "once" without a notification or for one-time rules (#4965). */
export const repetitionLocked = (rule: TaskRule) => !hasNotify(rule.definition) || isOneTimeRule(rule.definition);

/** The first problem with the rule, so the Apply message can say what is wrong. */
export function ruleError(rule: TaskRule, ctx: RuleEditorContext): string | null {
  if (blank(rule.name)) return ERR.name;
  // An inactive rule may stay a draft (e.g. a migrated display condition that needs checking).
  if (!rule.isActive) return null;
  const d = rule.definition;
  const conditions = d.groups.flatMap(g => g.conditions);
  if (conditions.length === 0) return ERR.noConditions;
  if (d.actions.length === 0) return ERR.noActions;
  for (const c of conditions) {
    const e = conditionError(c, ctx);
    if (e) return e;
  }
  for (const a of d.actions) {
    const e = actionError(a, ctx);
    if (e) return e;
  }
  return Object.values(timingErrors(rule))[0] ?? null;
}

export const ruleHasErrors = (rule: TaskRule, ctx: RuleEditorContext): boolean => ruleError(rule, ctx) != null;

/** Strips what does not apply any more, so the saved JSON matches what the editor shows. */
export function normalizeForSave(rule: TaskRule): TaskRule {
  const copy = cloneRule(rule);
  copy.name = copy.name.trim();
  copy.definition.groups = copy.definition.groups.filter(g => g.conditions.length > 0);
  const t = copy.definition.timing;
  if (t.trigger !== 'Delayed') t.delayAmount = null;
  if (repetitionLocked(copy)) t.repeat = 'Once';
  if (t.repeat === 'Once') {
    t.interval = null;
    t.everyDays = null;
    t.maxNotifications = null;
  }
  if (t.interval !== 'Custom') t.everyDays = null;
  copy.definition.actions.forEach(a => {
    if (a.type === 'Notify') a.comment = blank(a.comment) ? null : a.comment!.trim();
  });
  return copy;
}

/* ===================== conflicts (#4964) ===================== */

const PROPERTY_OF: Partial<Record<TaskRuleActionType, string>> = {
  SetStatus: 'status',
  SetResponsible: 'responsible',
  SetResult: 'result',
  SetPriority: 'priority',
};

const actionValue = (a: TaskRuleAction): string | null => {
  switch (a.type) {
    case 'SetStatus':
      return a.status ?? null;
    case 'SetResponsible':
      return a.userEmail ?? (a.departmentId != null ? `d${a.departmentId}` : null);
    case 'SetResult':
      return a.value ?? null;
    case 'SetPriority':
      return a.priority != null ? String(a.priority) : null;
  }
  return null;
};

/** Other active rules of the same task that set the same property to a different value. */
export function conflictsFor(rule: TaskRule, action: TaskRuleAction, rules: TaskRule[]): TaskRule[] {
  const property = action.type && PROPERTY_OF[action.type];
  const value = actionValue(action);
  if (!property || value == null || !rule.isActive) return [];
  return rules.filter(
    other =>
      other !== rule &&
      other.isActive &&
      other.definition.actions.some(b => b.type === action.type && actionValue(b) != null && actionValue(b) !== value)
  );
}

/* ===================== plain-text preview ===================== */

/** Gaps in the preview are named after the input that is still missing, e.g. "[value]". */
const GAPS = () => ({
  condition: $localize`:@@ruleGapCondition:[condition]`,
  comparison: $localize`:@@ruleGapComparison:[comparison]`,
  value: $localize`:@@ruleGapValue:[value]`,
  number: $localize`:@@ruleGapNumber:[number]`,
  task: $localize`:@@ruleGapTask:[task]`,
  status: $localize`:@@ruleGapStatus:[status]`,
  option: $localize`:@@ruleGapOption:[option]`,
  contact: $localize`:@@ruleGapContact:[contact]`,
  action: $localize`:@@ruleGapAction:[action]`,
  recipient: $localize`:@@ruleGapRecipient:[recipient]`,
  department: $localize`:@@ruleGapDepartment:[department]`,
  person: $localize`:@@ruleGapPerson:[person]`,
  result: $localize`:@@ruleGapResult:[result]`,
  priority: $localize`:@@ruleGapPriority:[priority]`,
  title: $localize`:@@ruleGapTitle:[title]`,
});
type Gap = keyof ReturnType<typeof GAPS>;

const isMissing = (v: string | number | null | undefined): v is null | undefined | '' => v === null || v === undefined || v === '';

/** The value, or the placeholder for what is missing. */
const orGap = (v: string | number | null | undefined, gap: Gap) => (isMissing(v) ? GAPS()[gap] : String(v));

/** A chosen value in quotes, or the placeholder (never quoted, so it reads as a gap). */
const quoted = (v: string | number | null | undefined, gap: Gap) =>
  isMissing(v) ? GAPS()[gap] : $localize`:@@ruleQuoted:“${String(v)}:value:”`;

/** "5 days" in the form an "in less than …" / "for at least …" clause needs. */
export function duration(amount: number | null | undefined, unit: TaskRuleUnit | null | undefined): string {
  const n = orGap(amount, 'number');
  const one = Number(amount) === 1;
  switch (unit) {
    case 'Weeks':
      return one ? $localize`:@@ruleDurWeek:${n}:n: week` : $localize`:@@ruleDurWeeks:${n}:n: weeks`;
    case 'Months':
      return one ? $localize`:@@ruleDurMonth:${n}:n: month` : $localize`:@@ruleDurMonths:${n}:n: months`;
    default:
      return one ? $localize`:@@ruleDurDay:${n}:n: day` : $localize`:@@ruleDurDays:${n}:n: days`;
  }
}

/** The condition's value as it appears in the sentence: quoted names, plain numbers. */
function valueText(c: TaskRuleCondition, ctx: RuleEditorContext, kind: ValueKind | null): string {
  switch (kind) {
    case 'status':
    case 'durationStatus':
      return quoted(c.value ? statusLabel(c.value) : null, 'status');
    case 'option':
      return quoted(c.value, 'option');
    case 'text':
      return quoted(c.value, 'value');
    case 'contractType':
      return quoted(c.value ? (ctx.contractTypes.find(x => String(x.id) === c.value)?.name ?? c.value) : null, 'value');
    case 'contractStatus':
      return quoted(c.value ? (ctx.contractStatuses.find(x => String(x.id) === c.value)?.status ?? c.value) : null, 'value');
    case 'contact':
      return orGap(c.value ? (ctx.contactName(c.value, c.contactType) ?? c.value) : null, 'contact');
  }
  return orGap(c.value, 'value');
}

/** Subject + predicate of one condition, e.g. "the due date is in less than 5 days". */
export function conditionText(c: TaskRuleCondition, ctx: RuleEditorContext): string {
  if (!c.field) return GAPS().condition;
  const kind = valueKindOf(c, ctx);
  const value = valueText(c, ctx, kind);
  const dur = duration(c.amount, c.unit);
  if (c.field === 'OtherTask') {
    const other = ctx.otherTasks.find(t => t.id === c.otherTaskConfigId);
    const task = quoted(other ? `${other.nr ? other.nr + ' ' : ''}${other.title ?? ''}`.trim() : null, 'task');
    if (c.property !== 'Result') {
      return c.operator === 'IsNot'
        ? $localize`:@@rulePvOtherStatusNot:the task ${task}:task: does not have the status ${value}:value:`
        : $localize`:@@rulePvOtherStatus:the task ${task}:task: has the status ${value}:value:`;
    }
    return $localize`:@@rulePvOtherResult:the result of the task ${task}:task: ${resultPredicate(c, value, dur)}:predicate:`;
  }
  const subject = SUBJECT_LABELS[c.field] ?? fieldLabel(c.field);
  switch (c.operator) {
    case 'InLessThan':
      return $localize`:@@rulePvInLessThan:${subject}:subject: is in less than ${dur}:duration:`;
    case 'IsToday':
      return $localize`:@@rulePvIsToday:${subject}:subject: is today`;
    case 'OverdueSince':
      return $localize`:@@rulePvOverdueSince:${subject}:subject: has been overdue for at least ${dur}:duration:`;
    case 'SinceAtLeast':
      return $localize`:@@rulePvSinceAtLeast:the status has been ${value}:value: for at least ${dur}:duration:`;
    case 'IsReset':
      return $localize`:@@rulePvIsReset:the task has been reset`;
    case 'NotAssigned':
      return $localize`:@@rulePvNotAssigned:the task is not assigned to anyone`;
    case 'NotAssignedSince':
      return $localize`:@@rulePvNotAssignedSince:the task has not been assigned to anyone for ${dur}:duration:`;
    case 'AllDone':
      return $localize`:@@rulePvAllDone:all subtasks are done`;
  }
  return $localize`:@@rulePvGeneric:${subject}:subject: ${resultPredicate(c, value, dur)}:predicate:`;
}

function resultPredicate(c: TaskRuleCondition, value: string, dur: string): string {
  switch (c.operator) {
    case 'Is':
      return $localize`:@@rulePrIs:is ${value}:value:`;
    case 'IsNot':
      return $localize`:@@rulePrIsNot:is not ${value}:value:`;
    case 'IsYes':
      return $localize`:@@rulePrIsYes:is “Yes”`;
    case 'IsNo':
      return $localize`:@@rulePrIsNo:is “No”`;
    case 'Gt':
      return $localize`:@@rulePrGt:is greater than ${value}:value:`;
    case 'Ge':
      return $localize`:@@rulePrGe:is at least ${value}:value:`;
    case 'Lt':
      return $localize`:@@rulePrLt:is less than ${value}:value:`;
    case 'Le':
      return $localize`:@@rulePrLe:is at most ${value}:value:`;
    case 'Eq':
      return $localize`:@@rulePrEq:equals ${value}:value:`;
    case 'Between':
      return $localize`:@@rulePrBetween:is between ${value}:value: and ${orGap(c.value2, 'value')}:value2:`;
    case 'Contains':
      return $localize`:@@rulePrContains:contains ${value}:value:`;
    case 'NotContains':
      return $localize`:@@rulePrNotContains:does not contain ${value}:value:`;
    case 'InLessThan':
      return $localize`:@@rulePrInLessThan:is in less than ${dur}:duration:`;
    case 'OverdueSince':
      return $localize`:@@rulePrOverdueSince:has been overdue for at least ${dur}:duration:`;
    case 'BeforeDueDate':
      return $localize`:@@rulePrBeforeDue:is before the due date`;
    case 'AfterDueDate':
      return $localize`:@@rulePrAfterDue:is after the due date`;
  }
  return GAPS().comparison;
}

export function conditionsText(d: TaskRuleDefinition, ctx: RuleEditorContext): string {
  const groups = d.groups.filter(g => g.conditions.length > 0);
  if (groups.length === 0) return GAPS().condition;
  const and = $localize`:@@ruleWordAnd:and`;
  const or = $localize`:@@ruleWordOr:or`;
  return groups
    .map(g => {
      const text = g.conditions.map(c => conditionText(c, ctx)).join(` ${and} `);
      return groups.length > 1 && g.conditions.length > 1 ? `(${text})` : text;
    })
    .join(` ${or} `);
}

/** "a", "a and b", "a, b and c". */
function listText(parts: string[]): string {
  const and = $localize`:@@ruleWordAnd:and`;
  return parts.length < 2 ? (parts[0] ?? '') : `${parts.slice(0, -1).join(', ')} ${and} ${parts[parts.length - 1]}`;
}

const departmentName = (ctx: RuleEditorContext, id: number | null | undefined) =>
  orGap(ctx.departments.find(d => d.id === id)?.name, 'department');

const userName = (ctx: RuleEditorContext, email: string | null | undefined) =>
  orGap(email ? (ctx.users.find(u => u.emailAddress === email)?.displayName ?? email) : null, 'person');

/** Who gets notified, in the accusative ("notify …"). */
function recipientText(a: TaskRuleAction, ctx: RuleEditorContext): string {
  switch (a.recipient) {
    case 'Responsible':
      return $localize`:@@rulePvRecResponsible:the assigned person`;
    case 'Manager':
      return $localize`:@@rulePvRecManager:the manager of the assigned person (Reports to)`;
    case 'TaskDepartment':
      return $localize`:@@rulePvRecTaskDepartment:the person from the task's department`;
    case 'Department':
      return $localize`:@@rulePvRecDepartment:the person from the department ${departmentName(ctx, a.departmentId)}:department:`;
    case 'User':
      return userName(ctx, a.userEmail);
  }
  return GAPS().recipient;
}

/** One action as an infinitive phrase, e.g. "notify the responsible person". */
export function actionText(a: TaskRuleAction, ctx: RuleEditorContext): string {
  switch (a.type) {
    case 'Notify': {
      const to = recipientText(a, ctx);
      return a.comment
        ? $localize`:@@rulePvNotifyComment:notify ${to}:recipient: with the comment “${a.comment}:comment:”`
        : $localize`:@@rulePvNotify:notify ${to}:recipient:`;
    }
    case 'SetStatus': {
      const status = quoted(a.status ? statusLabel(a.status) : null, 'status');
      return ctx.task.type === TaskTypeEnum.Group
        ? $localize`:@@rulePvSetStatusGroup:set the status of the group and all its subtasks to ${status}:status:`
        : $localize`:@@rulePvSetStatus:set the status to ${status}:status:`;
    }
    case 'SetResponsible': {
      const who = a.userEmail
        ? userName(ctx, a.userEmail)
        : a.departmentId != null
          ? $localize`:@@rulePvDeptPerson:the person from the department ${departmentName(ctx, a.departmentId)}:department:`
          : GAPS().person;
      return $localize`:@@rulePvSetResponsible:assign the task to ${who}:who:`;
    }
    case 'SetResult': {
      const value = quoted(a.value, 'result');
      return ctx.task.validation
        ? $localize`:@@rulePvSetResultReview:set the result to ${value}:value: (the task then goes to review)`
        : $localize`:@@rulePvSetResult:set the result to ${value}:value:`;
    }
    case 'SetPriority': {
      const priority = quoted(PRIORITIES.find(p => p.value === a.priority)?.text, 'priority');
      return $localize`:@@rulePvSetPriority:set the priority to ${priority}:priority:`;
    }
    case 'CreateMicrotask': {
      const title = quoted(a.title, 'title');
      return a.validationRequired
        ? $localize`:@@rulePvMicrotaskReview:create the microtask ${title}:title: (review required)`
        : $localize`:@@rulePvMicrotask:create the microtask ${title}:title:`;
    }
  }
  return GAPS().action;
}

const INTERVAL_TEXT = () => ({
  Daily: $localize`:@@rulePvDaily:daily`,
  Weekly: $localize`:@@rulePvWeekly:weekly`,
  Monthly: $localize`:@@rulePvMonthly:monthly`,
});

/**
 * The whole rule as one sentence: "If …, then …." Missing input shows as a named
 * placeholder, so the sentence is readable from the first click on.
 */
export function previewText(rule: TaskRule, ctx: RuleEditorContext): string {
  const d = rule.definition;
  const t = d.timing;
  const conditions = conditionsText(d, ctx);
  const actions = d.actions.length ? listText(d.actions.map(a => actionText(a, ctx))) : GAPS().action;
  let text =
    t.trigger === 'Delayed'
      ? $localize`:@@rulePvRuleDelayed:If ${conditions}:conditions:, continuously for at least ${duration(t.delayAmount, t.delayUnit)}:duration:, then ${actions}:actions:.`
      : $localize`:@@rulePvRule:If ${conditions}:conditions:, then ${actions}:actions:.`;
  if (hasNotify(d) && !isOneTimeRule(d)) {
    if (t.repeat === 'Repeat') {
      const every =
        t.interval === 'Custom'
          ? $localize`:@@rulePvEveryDays:every ${orGap(t.everyDays, 'number')}:n: days`
          : (INTERVAL_TEXT() as Record<string, string>)[t.interval ?? ''] ?? GAPS().value;
      text += ' ' + $localize`:@@rulePvRepeat:The notification is repeated ${every}:every:, at most ${orGap(t.maxNotifications, 'number')}:max: times in total.`;
    }
    text += ' ' + $localize`:@@rulePvEnd:Notifications stop as soon as the task is “Done” or “Not required” or the conditions no longer apply.`;
  } else if (hasNotify(d)) {
    text += ' ' + $localize`:@@rulePvOnce:This rule runs once per task.`;
  }
  return text;
}

export function summaryText(rule: TaskRule, ctx: RuleEditorContext): string {
  const d = rule.definition;
  return d.groups.some(g => g.conditions.length > 0)
    ? $localize`:@@ruleSummaryIf:If ${conditionsText(d, ctx)}:conditions:`
    : $localize`:@@ruleSummaryNone:No condition yet`;
}
